import Section, { Reveal } from '../components/Section';
import PhoneMock from '../components/PhoneMock';
import DesktopMock from '../components/DesktopMock';
import { IconAndroid, IconWindows, IconApple, IconDl, IconLinux } from '../components/Icon';

type Plat = 'android' | 'windows' | 'ios' | 'linux' | 'other';

const PLAY_URL = 'https://play.google.com/store/apps/details?id=com.tnidu.vpnee';
const WIN_URL  = 'https://vnav.lk/api/vpnee/downloads/Vpnee_Setup_1.0.2.exe';

type Card = {
  key: Plat;
  icon: any;
  label: string;
  meta: string;
  cta: string;
  href: string;
  external?: boolean;
  badge?: string;
  disabled?: boolean;
};

const CARDS: Card[] = [
  { key: 'windows', icon: IconWindows, label: 'Vpnee for Windows',  meta: 'Direct installer · Windows 10+ · ~161 MB',     cta: 'Download .exe',         href: WIN_URL, external: true },
  { key: 'android', icon: IconAndroid, label: 'Vpnee for Android',  meta: 'Play Store · Android 6+ · ~26 MB',           cta: 'Get it on Google Play', href: PLAY_URL, external: true },
  { key: 'ios',     icon: IconApple,   label: 'Vpnee for iOS',      meta: 'TestFlight — next release',                  cta: 'Join TestFlight',        href: '#notify',        badge: 'Coming soon', disabled: true },
  { key: 'linux',   icon: IconLinux,   label: 'Vpnee for Linux',    meta: 'AppImage · .deb · .rpm · glibc 2.31+',         cta: 'Notify me',             href: '#notify',        badge: 'Coming soon', disabled: true },
];

/**
 * Google Play badge — inlined SVG so we don't ship a raster asset and it
 * scales to any DPI. Follows Google's brand guidelines for size + spacing.
 */
function PlayBadge() {
  return (
    <span className="inline-flex items-center gap-3 px-5 py-3 rounded-xl bg-black border border-white/15 shadow-lg hover:border-white/30 transition">
      <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden>
        <defs>
          <linearGradient id="pb1" x1="0" x2="1" y1="0" y2="1"><stop offset="0" stopColor="#00E5FF"/><stop offset="1" stopColor="#00B0FF"/></linearGradient>
          <linearGradient id="pb2" x1="0" x2="1" y1="0" y2="1"><stop offset="0" stopColor="#FFD600"/><stop offset="1" stopColor="#FF8F00"/></linearGradient>
          <linearGradient id="pb3" x1="0" x2="1" y1="0" y2="1"><stop offset="0" stopColor="#FF3D00"/><stop offset="1" stopColor="#C62828"/></linearGradient>
          <linearGradient id="pb4" x1="0" x2="1" y1="0" y2="1"><stop offset="0" stopColor="#00C853"/><stop offset="1" stopColor="#00E676"/></linearGradient>
        </defs>
        <path fill="url(#pb1)" d="M3.6 1.9c-.4.4-.6 1-.6 1.7v16.8c0 .7.2 1.3.6 1.7l.1.1L13 12v-.1L3.7 1.8l-.1.1z"/>
        <path fill="url(#pb2)" d="M16 15.3L13 12v-.1l3-3.1.1.1 3.7 2.1c1 .6 1 1.6 0 2.2l-3.7 2.1z"/>
        <path fill="url(#pb3)" d="M16.1 15.2L13 12 3.6 21.4c.4.4 1 .4 1.7 0l10.8-6.2"/>
        <path fill="url(#pb4)" d="M16.1 8.8L5.3 2.6c-.7-.4-1.3-.4-1.7 0L13 12l3.1-3.2z"/>
      </svg>
      <span className="text-left leading-tight">
        <span className="block text-[10px] uppercase tracking-widest text-white/70">Get it on</span>
        <span className="block text-white font-[family-name:var(--font-display)] font-bold text-lg -mt-0.5">Google Play</span>
      </span>
    </span>
  );
}

export default function Download() {
  return (
    <>
      <Section chip="Get Vpnee" title="Download for your device." subtitle="Real native tunnel. No webview shim. Free tier works right after install — no card, no email required.">
        {/* Two previews — Windows desktop (left) + Android phone (right) */}
        <div className="grid gap-10 lg:gap-8 lg:grid-cols-2 items-stretch">

          {/* ── Windows side ── */}
          <Reveal>
            <div className="h-full flex flex-col items-center">
              <div className="flex-1 w-full grid place-items-center pb-6"><DesktopMock /></div>
              <div className="mt-auto w-full">
                <div className="w-full glass p-5 flex flex-col sm:flex-row sm:items-center gap-4 sm:min-h-[104px]">
                  <div className="w-12 h-12 shrink-0 rounded-2xl grid place-items-center bg-gradient-to-br from-[color:var(--color-brand)] to-[color:var(--color-brand-deep)] text-white shadow-lg">
                    <IconWindows width={24} height={24} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] uppercase tracking-widest text-[color:var(--color-brand-glow)] font-semibold">Recommended for desktop</div>
                    <div className="h-title text-xl font-extrabold">Vpnee for Windows</div>
                    <div className="text-sm text-[color:var(--color-ink-dim)] mt-0.5 truncate">Direct installer · Windows 10+ · ~78 MB</div>
                  </div>
                  <a href={WIN_URL} target="_blank" rel="noopener" className="btn btn-primary shrink-0 justify-center"><IconDl width={18} height={18} /> Download .exe</a>
                </div>
                <div className="mt-2 w-full px-1 text-xs text-[color:var(--color-ink-dim)] truncate">
                  <strong className="text-white">SHA-256</strong> <code className="text-[color:var(--color-brand-glow)] font-mono">d3f1…8b42</code> · <strong className="text-white">Signed</strong> TNIDU Inc. (EV cert)
                </div>
              </div>
            </div>
          </Reveal>

          {/* ── Android side ── */}
          <Reveal delay={0.15}>
            <div className="h-full flex flex-col items-center">
              <div className="flex-1 w-full grid place-items-center pb-6"><PhoneMock state="connected" /></div>
              <div className="mt-auto w-full">
                <div className="w-full glass p-5 flex flex-col sm:flex-row sm:items-center gap-4 sm:min-h-[104px]">
                  <div className="w-12 h-12 shrink-0 rounded-2xl grid place-items-center bg-gradient-to-br from-[color:var(--color-brand)] to-[color:var(--color-brand-deep)] text-white shadow-lg">
                    <IconAndroid width={24} height={24} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] uppercase tracking-widest text-[color:var(--color-brand-glow)] font-semibold">Recommended for mobile</div>
                    <div className="h-title text-xl font-extrabold">Vpnee for Android</div>
                    <div className="text-sm text-[color:var(--color-ink-dim)] mt-0.5 truncate">Play Store · Android 6+ · ~26 MB</div>
                  </div>
                  <a href={PLAY_URL} target="_blank" rel="noopener" className="shrink-0" aria-label="Get it on Google Play"><PlayBadge /></a>
                </div>
                <div className="mt-2 w-full px-1 text-xs text-[color:var(--color-ink-dim)]">Signed release · auto-updates via Play · free tier one tap after install.</div>
              </div>
            </div>
          </Reveal>
        </div>
      </Section>

      <Section chip="All platforms" title="Every device covered.">
        <div id="notify" className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {CARDS.map((c, i) => {
            const IconC = c.icon;
            const isPlay = c.key === 'android';
            return (
              <Reveal key={c.key} delay={i * 0.06}>
                <a
                  href={c.href}
                  target={c.external ? '_blank' : undefined}
                  rel={c.external ? 'noopener' : undefined}
                  className={`glass p-6 h-full block transition ${c.disabled ? 'opacity-70 cursor-not-allowed' : 'hover:border-[color:var(--color-brand)]/40'}`}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-11 h-11 rounded-xl grid place-items-center bg-white/5 border border-[color:var(--color-line)] text-[color:var(--color-brand-glow)]">
                      <IconC />
                    </div>
                    {c.badge && <div className="chip">{c.badge}</div>}
                  </div>
                  <div className="font-[family-name:var(--font-display)] font-bold text-lg">{c.label}</div>
                  <div className="text-sm text-[color:var(--color-ink-dim)] mt-1">{c.meta}</div>
                  <div className="mt-5">
                    {isPlay ? (
                      <PlayBadge />
                    ) : (
                      <div className="btn btn-ghost justify-center w-full"><IconDl width={16} height={16} /> {c.cta}</div>
                    )}
                  </div>
                </a>
              </Reveal>
            );
          })}
        </div>
        <div className="mt-6 text-sm text-[color:var(--color-ink-dim)] text-center max-w-2xl mx-auto">
          Linux support drops this quarter (AppImage, .deb, .rpm). iOS opens in TestFlight next release. Drop an email to <a className="text-[color:var(--color-brand-glow)] underline" href="mailto:hello@tnidu.com">hello@tnidu.com</a> and we'll ping you when they land.
        </div>
      </Section>

      <Section chip="Same on every device" title="One connect, live everywhere.">
        <div className="grid gap-4 sm:grid-cols-2">
          {[
            { t: 'Quick Settings tile', b: 'Pull down the status bar, tap Vpnee. Uptime + throughput right there. No need to open the app.' },
            { t: 'Home widgets — two sizes', b: 'Compact toggle + 4×2 live-stats variant. Auto-refresh on every state change; not capped at Android\'s 30-min widget interval.' },
            { t: 'Live foreground notification', b: 'Persistent notif with server label, uptime, throughput, and a one-tap Disconnect action. Bigtext body expands.' },
            { t: 'Event alerts', b: 'Data-cap approaching, Pro expiring soon, tunnel dropped mid-session, coupon applied — pushed to your notification tray.' },
            { t: 'Deep-link OAuth', b: 'Google / Facebook login opens in the system browser and hands back cleanly via vpnee:// deep-link. No embedded webview auth.' },
            { t: 'Zero-config VPN service', b: 'Grant the VPN permission once. Never asked again. Kill switch on by default.' },
          ].map((f) => (
            <div key={f.t} className="glass p-6">
              <h3 className="font-[family-name:var(--font-display)] font-bold text-lg mb-2">{f.t}</h3>
              <p className="text-sm text-[color:var(--color-ink-dim)] leading-relaxed">{f.b}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section chip="Windows client preview" title="The desktop, in full.">
        <Reveal>
          <DesktopMock />
          <div className="mt-4 text-center text-xs uppercase tracking-widest text-[color:var(--color-ink-faint)]">Windows client preview · actual UI may vary</div>
        </Reveal>
      </Section>

      <Section chip="Verify + trust" title="What you're actually downloading.">
        <div className="grid gap-4 md:grid-cols-3">
          <div className="glass p-6">
            <h3 className="font-[family-name:var(--font-display)] font-bold text-lg mb-2">Signed builds</h3>
            <p className="text-sm text-[color:var(--color-ink-dim)] leading-relaxed">Android APK signed by TNIDU Inc.'s Play signing key. Windows installer signed with an EV cert — no SmartScreen scare on first run.</p>
          </div>
          <div className="glass p-6">
            <h3 className="font-[family-name:var(--font-display)] font-bold text-lg mb-2">Reproducible core</h3>
            <p className="text-sm text-[color:var(--color-ink-dim)] leading-relaxed">The Xray binary we ship is the unmodified official upstream build. Hash it yourself against the release page; it will match.</p>
          </div>
          <div className="glass p-6">
            <h3 className="font-[family-name:var(--font-display)] font-bold text-lg mb-2">Minimum permissions</h3>
            <p className="text-sm text-[color:var(--color-ink-dim)] leading-relaxed">Android permissions: Internet, VPN, Foreground Service, Notifications. No contacts, no camera, no location, no ads SDK.</p>
          </div>
        </div>
      </Section>
    </>
  );
}
